import { officeDisplays } from "../catalog/office.ts";
import { routeCatalog } from "../catalog/route-catalog.ts";
import type { RouteDefinition } from "../catalog/route-definition.ts";
import { routeParameter } from "../route-parameter.ts";
import type { MatchCandidate, MatchedRoute } from "./matched-route.ts";
import { areasByBucket } from "./route-definitions.ts";

const catalog: Readonly<Record<string, RouteDefinition>> = routeCatalog;
const officeDisplayNames: ReadonlySet<string> = new Set(officeDisplays);

function hasParameters({
  match,
  definition,
}: {
  readonly match: MatchCandidate;
  readonly definition: RouteDefinition;
}): boolean {
  return definition.path.split("/").every((segment) => {
    const parameter = routeParameter(segment);

    if (!parameter) return true;

    // Static navigation owns section even when an incoming alias names that parameter.
    if (parameter.key === "section" && match.area !== "game") return true;

    const value = match[parameter.key];

    if (parameter.optional && value === undefined) return true;

    if (parameter.wildcard)
      return (
        Array.isArray(value) && value.every((part) => typeof part === "string")
      );

    return typeof value === "string";
  });
}

function hasGameOwnership({
  match,
  definition,
}: {
  readonly match: MatchCandidate;
  readonly definition: RouteDefinition;
}): boolean {
  if (definition.scope === "static") return false;

  if (match.catalogScope !== "listed" && match.catalogScope !== "unlisted")
    return false;

  const unlisted = match.catalogScope === "unlisted";
  return (
    typeof match.gameSlug === "string" &&
    typeof match.section === "string" &&
    (match.league === undefined || typeof match.league === "string") &&
    match.bucket === (unlisted ? "unlisted" : "app") &&
    match.entry === (unlisted ? "direct" : definition.entry)
  );
}

function hasStaticOwnership({
  match,
  definition,
}: {
  readonly match: MatchCandidate;
  readonly definition: RouteDefinition;
}): boolean {
  if (definition.scope === "game" || match.name === "episodeReplay")
    return false;

  if (
    match.name === "office.display" &&
    (typeof match.display !== "string" ||
      !officeDisplayNames.has(match.display))
  )
    return false;

  if (
    match.name === "policy" &&
    match.version !== undefined &&
    typeof match.version !== "number"
  )
    return false;

  return (
    match.area === areasByBucket[definition.bucket] &&
    match.section === null &&
    match.bucket === definition.bucket &&
    match.entry === definition.entry
  );
}

export function isMatchedRoute(match: MatchCandidate): match is MatchedRoute {
  if (typeof match.name !== "string" || !Object.hasOwn(catalog, match.name))
    return false;

  const definition = catalog[match.name];

  if (!definition || match.disposition !== definition.disposition) return false;

  if (!hasParameters({ match, definition })) return false;

  return match.area === "game"
    ? hasGameOwnership({ match, definition })
    : hasStaticOwnership({ match, definition });
}
